import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crosshair, Sticker, Type, AudioLines, Music2 } from "lucide-react";
import { API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { StickerGrid } from "@/components/editor/stickers/StickerGrid";
import { CustomSticker } from "@/components/editor/stickers/CustomSticker";
import { SfxGrid } from "@/components/editor/stickers/SfxGrid";
import { StickerOptions } from "@/components/editor/stickers/StickerOptions";
import { PlacedList } from "@/components/editor/stickers/PlacedList";
import { MusicPanel } from "@/components/editor/stickers/MusicPanel";
import { useAudioFavs } from "@/components/editor/stickers/FavStar";
import { toFree } from "@/components/editor/StickerLayer";

export { stickerUrl, sfxUrl, playSfx } from "@/components/editor/stickers/fx";

const TABS = { stickers: [["stickers", Sticker], ["custom", Type]], audio: [["sfx", AudioLines], ["music", Music2]] };
const EMPTY = { stickers: [], sfx: [], anims: [], styles: [], sticker_cats: [], sfx_cats: [], music: [], music_cats: [] };

export const StickerSfxPanel = ({ cfg, setCfg, rel, len, mode = "stickers", onAdded, onRemoved }) => {
  const { t } = useI18n();
  const [cat, setCat] = useState(EMPTY);
  const tabs = TABS[mode];
  const [tab, setTab] = useState(tabs[0][0]);
  const fav = useAudioFavs();
  const [opt, setOpt] = useState({ pos: "center", anim: "pop", size: "m", d: 1.6 });
  useEffect(() => { fetch(`${API}/fx/catalog`).then((r) => r.json()).then(setCat).catch(() => {}); }, []);
  const at = Math.max(0, Number(rel.toFixed(2)));
  const max = { stickers: 8, sfx: 12 };
  const add = (k, item) => {
    if ((cfg[k] || []).length >= max[k]) { toast.error(t("st.limit").replace("{n}", max[k])); return; }
    setCfg((c) => ({ ...c, [k]: [...(c[k] || []), item] }));
  };
  const addSticker = (s) => {
    if ((cfg.stickers || []).length >= max.stickers) { toast.error(t("st.limit").replace("{n}", max.stickers)); return; }
    onAdded?.((cfg.stickers || []).length);
    add("stickers", toFree({ ...s, ...opt, t: at }));
  };
  const update = (k, i, patch) => setCfg((c) => ({ ...c, [k]: c[k].map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
  const remove = (k, i) => { if (k === "stickers") onRemoved?.(); setCfg((c) => ({ ...c, [k]: c[k].filter((_, j) => j !== i) })); };

  return (
    <div className="space-y-3" data-testid={mode === "stickers" ? "stickers-sfx-panel" : "audio-panel"}>
      {mode === "stickers" && <p className="text-[11px] text-[#3ad7ff]/80" data-testid="stickers-drag-hint">{t("st.drag_hint")}</p>}
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-slate-500">{t("st.title")} · <span className="font-mono text-[#3ad7ff]">+{at}s</span>
          <span className="ml-2 font-mono text-slate-600" data-testid="stfx-counter">{(cfg.stickers || []).length}/8 · {(cfg.sfx || []).length}/12</span></div>
        <button data-testid="set-impact-btn" onClick={() => setCfg((c) => ({ ...c, impact: at }))} className="cx-ghost rounded-full px-3 py-1.5 text-[11px] flex items-center gap-1.5"><Crosshair className="w-3.5 h-3.5 text-[#ff4d6d]" /> {t("st.impact")}{cfg.impact != null ? ` (${cfg.impact}s)` : ""}</button>
      </div>
      <div className="grid grid-cols-2 gap-1 rounded-lg bg-black/30 p-1" data-testid="stickers-sfx-tabs">
        {tabs.map(([k, Icon]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`stfx-tab-${k}`}
            className={`flex items-center justify-center gap-1.5 rounded-md py-1.5 text-[11px] font-semibold transition-colors ${tab === k ? "bg-[#2f7bff]/25 text-white shadow-[inset_0_0_0_1px_rgba(58,215,255,0.4)]" : "text-slate-500 hover:text-slate-300"}`}>
            <Icon className="w-3.5 h-3.5" />{t(`st.tab_${k}`)}
          </button>
        ))}
      </div>
      {(tab === "stickers" || tab === "custom") && <StickerOptions opt={opt} setOpt={setOpt} anims={cat.anims} />}
      {tab === "stickers" && <StickerGrid cat={cat} onAdd={addSticker} />}
      {tab === "custom" && <CustomSticker styles={cat.styles} onAdd={addSticker} />}
      {tab === "sfx" && <SfxGrid cat={cat} {...fav} onAdd={(id) => add("sfx", { id, t: at, vol: 1 })} />}
      {tab === "music" && <MusicPanel cat={cat} cfg={cfg} setCfg={setCfg} len={len} {...fav} />}
      <PlacedList cfg={cfg} cat={cat} update={update} remove={remove} only={mode === "stickers" ? "stickers" : "sfx"} />
    </div>
  );
};
